import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { installAuthInterceptors } from "@/lib/authInterceptor";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Install the 401 refresh-and-retry interceptor on the shared axios default
// (idempotent) so every raw axios.* call across the app recovers an expired
// session instead of silently failing or dumping the user mid-action.
installAuthInterceptors(axios);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistSession = useCallback((data) => {
    setUser(data || false);
  }, []);

  const clearSession = useCallback(() => {
    setUser(false);
  }, []);

  const fetchMe = useCallback(async () => {
    return axios.get(`${API}/api/auth/me`, {
      withCredentials: true
    });
  }, []);

  const checkAuth = useCallback(async () => {
    // Form-link pages (/form/{token}) authenticate by token, not by user cookies.
    // Skipping the auth handshake on those routes avoids the 401 noise in audit logs
    // and saves two HTTP round-trips for the employee.
    if (typeof window !== "undefined" && (
      window.location.pathname.startsWith("/form/")
      || window.location.pathname.startsWith("/privacy-training/")
      || window.location.pathname === "/new"
      || window.location.pathname === "/intake/student"
      || window.location.pathname === "/discount"
    )) {
      clearSession();
      setLoading(false);
      return;
    }
    try {
      const { data } = await fetchMe();
      setUser(data);
    } catch {
      try {
        const { data } = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        persistSession(data);
      } catch {
        clearSession();
      }
    } finally {
      setLoading(false);
    }
  }, [clearSession, fetchMe, persistSession]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    // A fresh login shows the "change your password" reminder again.
    try { sessionStorage.removeItem("pw_change_prompt_ok"); } catch { /* private mode */ }
    persistSession(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    try { sessionStorage.removeItem("pw_change_prompt_ok"); } catch { /* private mode */ }
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, updateUser: persistSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

// Axios helper with auth. The instance gets the same 401 refresh-and-retry
// behaviour as the shared default so components using useApi() recover too.
export function useApi() {
  return useMemo(() => {
    const instance = axios.create({
      baseURL: `${API}/api`,
      withCredentials: true,
    });
    installAuthInterceptors(instance);
    return instance;
  }, []);
}
